import React from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	Card,
	CardContent,
	Typography,
	Chip,
	Grid,
	Modal,
	Button,
	CardActions,
} from "@material-ui/core";
import {
	useIsOnline,
	isStaffUser,
	isConsultantUser,
	useCurrentOrganisation,
	useCurrentRole,
	useIsBornAccount,
	useRouter,
	isProd,
	dayDifference,
	formatDate,
	isSuperAdminUser,
} from "utils";
import { accountCountryLabel } from "../../utils/account-summary";
import { CrudOptions } from "frame/components";
import moment from "moment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import ReactGA from "react-ga";
import { HOME_ROUTE } from "features/home/home-routes";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		marginBottom: theme.spacing(2),
	},
	content: {
		position: "relative",
		padding: theme.spacing(2, 4),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: "#999999",
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		padding: theme.spacing(0, 0, 0.5, 0),
	},
	activeOrg: {
		backgroundColor: theme.palette.success.main,
		color: "#FFF",
		fontSize: "14px",
	},
	error: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
	warningText: {
		color: theme.palette.error.main,
	},
	success: {
		color: theme.palette.success.main,
	},
	gracePeriod: {
		color: "#856404",
		fontWeight: 500,
	},
	disabled: {
		color: theme.palette.error.main,
		fontWeight: 500,
	},
	link: {
		color: theme.palette.text.link,
		cursor: "pointer",
	},
	confirmModalRoot: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		outline: "none",
		boxShadow: theme.shadows[20],
		width: 400,
		maxWidth: "100%",
	},
	confirmModalBody: {
		padding: theme.spacing(2, 0),
	},
	confirmModalActions: {
		marginBottom: theme.spacing(2),
	},
	confirmModalCancel: {
		padding: theme.spacing(0.6, 3),
		background: theme.palette.grey[300],
		"&:hover": {
			background: theme.palette.grey[400],
		},
	},
	confirmModalDeactivate: {
		padding: theme.spacing(0.6, 3),
		color: theme.palette.common.white,
		background: theme.palette.error.main,
		"&:hover": {
			background: theme.palette.error.dark,
		},
	},
	confirmModalActivate: {
		padding: theme.spacing(0.6, 3),
		color: theme.palette.common.white,
		background: theme.palette.success.main,
		"&:hover": {
			background: theme.palette.success.dark,
		},
	},
}));

const OrganisationListing = ({
	className,
	organisation,
	onEdit,
	onDelete,
	onChangeOrganisation,
	onActivateDeactivate,
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const [pendingStatusChange, setPendingStatusChange] = React.useState(null);

	const isOnline = useIsOnline();
	const currentRole = useCurrentRole();
	const isBornAccount = useIsBornAccount();
	const currentOrganisationId = useCurrentOrganisation();

	const isSuperAdmin = isSuperAdminUser(currentRole);
	const isNotDemoAccount =
		organisation.name !== "Born Digital Demo" &&
		organisation.name !== "SafetyWise Demo";
	const allowedToEdit =
		!isStaffUser(currentRole) &&
		!isStaffUser(organisation.organisationRole) &&
		!isConsultantUser(organisation.organisationRole);
	const isCurrentOrganisation = organisation.id === currentOrganisationId;

	const daysRemaining = moment(organisation.createdAt)
		.add(30, "days")
		.diff(moment(), "days");
	const daysOld = moment().diff(moment(organisation.createdAt), "days");

	const onChangeOrganisationHandler =
		(organisationId, organisationName) => () => {
			onChangeOrganisation(organisationId);
			isProd &&
				ReactGA.event({
					category: "Menu",
					action: "Organization Switched",
					label: organisationName,
				});
			history.push(HOME_ROUTE);
		};

	return (
		<>
		<Card className={clsx(classes.root, className)}>
			<CardContent className={classes.content}>
				<Grid container spacing={2}>
					<Grid item md={2} sm={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Name
						</Typography>
						<Typography variant="h5" className={classes.value}>
							{organisation.name}{" "}
						</Typography>
					</Grid>

					<Grid item md={2} sm={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Country
						</Typography>
						<Typography variant="h5" className={classes.value}>
							{accountCountryLabel(organisation)}
						</Typography>
					</Grid>

					<Grid item md={2} sm={6} xs={12}>
					<Typography className={classes.label} variant="body2">
						Status
					</Typography>
					<Typography className={classes.value} variant="h5">
						<span
							className={
								organisation.lifecycleStatus === "Disabled"
									? classes.disabled
									: organisation.status
									? classes.success
									: classes.warningText
							}
						>
							{organisation.lifecycleStatus === "Disabled"
								? "Disabled"
								: organisation.status
								? "Active"
								: "Inactive"}
						</span>
					</Typography>
				</Grid>

				<Grid item md={1} sm={6} xs={12}>
					<Typography className={classes.label} variant="body2">
						Active Users
					</Typography>
					<Typography className={classes.value} variant="h5">
						{organisation.numberOfUsers}
					</Typography>
				</Grid>

				<Grid item md={1} sm={6} xs={12}>
					<Typography className={classes.label} variant="body2">
						Modules
					</Typography>
					<Typography className={classes.value} variant="h5">
						{organisation.enabledModules && organisation.enabledModules.length > 0
							? organisation.enabledModules.map(m => m === 'Equipment' ? 'Equip' : m).join(', ')
							: '-'}
					</Typography>
				</Grid>

					{/* <Grid item md={2} sm={6} xs={12}>
            <Typography className={classes.label} variant="body2">
              Consultant
            </Typography>
            <Typography className={classes.value} variant="h5">
              {organisation.consultant ? organisation.consultant : '-'}
            </Typography>
          </Grid> */}

					<Grid item md={2} sm={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Date Activated
						</Typography>
						<Typography className={classes.value} variant="h5">
							{formatDate(organisation.createdAt)}
						</Typography>
					</Grid>

					<Grid item md={2} sm={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Last Activity
						</Typography>
						<Typography className={classes.value} variant="h5">
							{organisation.lastActivityType && organisation.lastActivityDate
								? `${organisation.lastActivityType} - ${formatDate(organisation.lastActivityDate)}`
								: 'No activity'}
						</Typography>
					</Grid>

				<Grid item md={2} sm={6} xs={12}>
					<Typography className={classes.label} variant="body2">
						Subscription Status
					</Typography>
					<Typography className={classes.value} variant="h5">
						{organisation.lifecycleStatus === "GracePeriod" ? (
							<>
								<span className={classes.gracePeriod}>Payment Issue</span>
								{organisation.gracePeriodEndDate && (
									<Typography variant="caption" style={{ display: "block", color: "#856404", marginTop: 2 }}>
										Access restricted in {dayDifference(undefined, organisation.gracePeriodEndDate)} day{dayDifference(undefined, organisation.gracePeriodEndDate) !== 1 ? "s" : ""} ({formatDate(organisation.gracePeriodEndDate)})
									</Typography>
								)}
							</>
						) : organisation.lifecycleStatus === "Disabled" ? (
							<span className={classes.disabled}>Disabled</span>
						) : organisation.subscriptionStatus
							? organisation.subscriptionStatus === "active"
								? "Active"
								: organisation.subscriptionStatus === "trialing"
									? "Free Trial"
									: "Disabled"
							: "-"}
						{!organisation.lifecycleStatus || organisation.lifecycleStatus === "Active"
							? organisation.subscriptionEndDate
								? ` (${dayDifference(undefined, organisation.subscriptionEndDate)} Days Remaining)`
								: ""
							: ""}
					</Typography>
				</Grid>

				<Grid item md={1} sm={6} xs={12} style={{ display: "flex", alignItems: "center" }}>
					{isCurrentOrganisation ? (
						<Chip
							className={classes.activeOrg}
							size="small"
							label="Selected"
						/>
					) : (
						isOnline && (
							<Typography
								className={classes.link}
								variant="h5"
								onClick={onChangeOrganisationHandler(
									organisation.id,
									organisation.name
								)}
							>
								Go
							</Typography>
						)
					)}
				</Grid>

				{isBornAccount && (
					<Grid item md={2} sm={6} xs={12}>
						<Typography
							className={classes.label}
							variant="body2"
						>
							Creator Email Confirmed
						</Typography>
						<Typography className={classes.value} variant="h5">
							{organisation.createrEmailComfirmed
								? "Yes"
								: "No"}
							{!organisation.createrEmailComfirmed &&
								` (${daysOld} days old)`}
						</Typography>
					</Grid>
				)}

				<div className="ignore">
					<CrudOptions
							customRemoveModelText="Warning, deleting this organisation is permanent and cannot be undone."
							bornOnlyActions={["Delete"]}
							onEdit={() => onEdit(organisation.id)}
							onDelete={
								isBornAccount &&
								isNotDemoAccount &&
								allowedToEdit &&
								!isCurrentOrganisation
									? () => onDelete(organisation.id) // enabled in born superadmin only for easy maintenance
									: null
							}
							customOptions={
								isSuperAdmin && onActivateDeactivate
									? [
											{
												label: organisation.status
													? "Deactivate"
													: "Activate",
												icon: organisation.status
													? BlockIcon
													: CheckCircleIcon,
												onClick: () =>
													setPendingStatusChange({
														organisationId:
															organisation.id,
														name: organisation.name,
														status: !organisation.status,
													}),
											},
									  ]
									: []
							}
						/>
					</div>
				</Grid>
			</CardContent>
		</Card>

		<Modal
			open={!!pendingStatusChange}
			onClose={() => setPendingStatusChange(null)}
		>
			<Card className={classes.confirmModalRoot}>
				<CardContent>
					<Typography variant="h2">
						{pendingStatusChange?.status
							? "Confirm Activate"
							: "Confirm Deactivate"}
					</Typography>
					<Typography className={classes.confirmModalBody} variant="body1">
						{pendingStatusChange?.status ? (
							<>
								Are you sure you want to activate{" "}
								<strong>{pendingStatusChange?.name}</strong>?
								Users will be able to access this account again.
							</>
						) : (
							<>
								Are you sure you want to deactivate{" "}
								<strong>{pendingStatusChange?.name}</strong>?
							</>
						)}
					</Typography>
				</CardContent>
				<CardActions className={classes.confirmModalActions}>
					<Button
						className={
							pendingStatusChange?.status
								? classes.confirmModalActivate
								: classes.confirmModalDeactivate
						}
						variant="contained"
						onClick={() => {
							if (pendingStatusChange && onActivateDeactivate) {
								onActivateDeactivate(pendingStatusChange);
								setPendingStatusChange(null);
							}
						}}
					>
						{pendingStatusChange?.status
							? "Yes, Activate"
							: "Yes, Deactivate"}
					</Button>
					<Button
						className={classes.confirmModalCancel}
						variant="contained"
						onClick={() => setPendingStatusChange(null)}
					>
						Cancel
					</Button>
				</CardActions>
			</Card>
		</Modal>
	</>
	);
};

OrganisationListing.propTypes = {
	className: PropTypes.string,
	organisation: PropTypes.object.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onChangeOrganisation: PropTypes.func.isRequired,
	onActivateDeactivate: PropTypes.func,
};
export default OrganisationListing;
